const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOKEN_KEY = "mh_token";
export const getToken = () => {
  try { return localStorage.getItem(TOKEN_KEY); } catch (e) { return null; }
};
export const setToken = (t) => {
  try { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); } catch (e) { /* ignore */ }
};

async function request(path, options = {}) {
  const token = getToken();
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    let detail = `Something went wrong (${res.status}).`;
    try {
      const data = await res.json();
      if (typeof data.detail === "string") detail = data.detail;
      else if (Array.isArray(data.detail) && data.detail[0]?.msg) detail = data.detail[0].msg;
    } catch (e) {
      /* keep default */
    }
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
}

export const api = {
  me: () => request("/auth/me"),
  login: async (email, password) => {
    const data = await request("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
    if (data && data.token) setToken(data.token);
    return data;
  },
  logout: async () => {
    try { await request("/auth/logout", { method: "POST", body: "{}" }); }
    finally { setToken(null); }
  },
  currentAlignment: () => request("/alignment/current"),
  seasons: () => request("/seasons"),
  strategies: () => request("/strategies"),
  outcomes: () => request("/outcomes"),
  createSeason: (b) => request("/seasons", { method: "POST", body: JSON.stringify(b) }),
  setCurrentSeason: (id) => request(`/seasons/${id}/set-current`, { method: "POST", body: "{}" }),
  createStrategy: (b) => request("/strategies", { method: "POST", body: JSON.stringify(b) }),
  setCurrentStrategy: (id) => request(`/strategies/${id}/set-current`, { method: "POST", body: "{}" }),
  createOutcome: (b) => request("/outcomes", { method: "POST", body: JSON.stringify(b) }),
  setCurrentOutcome: (id) => request(`/outcomes/${id}/set-current`, { method: "POST", body: "{}" }),
  projects: () => request("/projects"),
  projectsByVenture: (vid) => request(`/projects?venture_id=${encodeURIComponent(vid)}`),
  unassignedProjects: () => request("/projects?unassigned=true"),
  ventures: () => request("/ventures"),
  createVenture: (b) => request("/ventures", { method: "POST", body: JSON.stringify(b) }),
  updateVenture: (id, b) => request(`/ventures/${id}`, { method: "PATCH", body: JSON.stringify(b) }),
  createProject: (b) => request("/projects", { method: "POST", body: JSON.stringify(b) }),
  updateProject: (id, b) => request(`/projects/${id}`, { method: "PATCH", body: JSON.stringify(b) }),
  changeProjectStatus: (id, new_status) => request(`/projects/${id}/status`, { method: "POST", body: JSON.stringify({ new_status }) }),
  setPrimaryDriver: (id) => request(`/projects/${id}/set-primary-driver`, { method: "POST", body: "{}" }),
  unsetPrimaryDriver: () => request("/alignment/primary-driver", { method: "DELETE" }),
  captures: () => request("/captures"),
  createCapture: (b) => request("/captures", { method: "POST", body: JSON.stringify(b) }),
  updateCapture: (id, b) => request(`/captures/${id}`, { method: "PATCH", body: JSON.stringify(b) }),
  clarifyCapture: (id, b) => request(`/captures/${id}/clarify`, { method: "POST", body: JSON.stringify(b) }),
  todayForDate: (date) => request(`/today-items?date=${encodeURIComponent(date)}`),
  allTodayItems: () => request("/today-items"),
  createTodayItem: (body) =>
    request("/today-items", { method: "POST", body: JSON.stringify(body) }),
  markDone: (id, completion_note) =>
    request(`/today-items/${id}/done`, {
      method: "POST",
      body: JSON.stringify(completion_note ? { completion_note } : {}),
    }),
  recommit: (id, date, why_here) =>
    request(`/today-items/${id}/recommit`, {
      method: "POST",
      body: JSON.stringify(why_here ? { date, why_here } : { date }),
    }),
  defer: (id, note) =>
    request(`/today-items/${id}/defer`, {
      method: "POST",
      body: JSON.stringify(note ? { note } : {}),
    }),
  remove: (id, note) =>
    request(`/today-items/${id}/remove`, {
      method: "POST",
      body: JSON.stringify(note ? { note } : {}),
    }),
};

export const TYPE_LABELS = {
  strategic_priority: "Strategic Priority",
  real_life_necessity: "Real-Life Necessity",
  blocker: "Blocker",
};

export const todayISO = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export const formatLongDate = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d); // local calendar day
  const weekday = dt.toLocaleDateString("en-US", { weekday: "long" });
  const month = dt.toLocaleDateString("en-US", { month: "long" });
  return `${weekday}, ${month} ${d}`;
};
